import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import GlossaryText from '../GlossaryText';
import Icon from '../Icon';
import styles from './ManualSearch.module.scss';

export const MANUAL_SEARCH_ENDPOINT = '/wt/api/main/v1/manual_search/';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlight = (text, query, className) => {
    if (!text || !query) {
        return <GlossaryText text={text} />;
    }

    const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'ig'));
    const needle = query.toLowerCase();

    return parts.map((part, index) =>
        part.toLowerCase() === needle ? (
            <mark key={index} className={className}>
                <GlossaryText text={part} />
            </mark>
        ) : (
            <GlossaryText key={index} text={part} />
        )
    );
};

// The query rides along so the page can highlight it; see ManualFindBar.
export const withQuery = (href, query) => {
    const [path, hash] = href.split('#');
    const search = new URLSearchParams({ q: query });

    return `${path}?${search}${hash ? `#${hash}` : ''}`;
};

const ManualSearch = ({
    manualId = null,
    placeholder = 'Search',
    endpoint = MANUAL_SEARCH_ENDPOINT,
}) => {
    const router = useRouter();
    // Mirrors ?q, which ManualFindBar highlights, so a reload or another
    // chapter still shows what was searched for.
    const urlQuery = typeof router.query.q === 'string' ? router.query.q : '';
    const previousUrlQueryRef = useRef(urlQuery);

    const [query, setQuery] = useState(urlQuery);
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const openOnResultsRef = useRef(!urlQuery);
    const resultRefs = useRef([]);

    const listboxId = 'manual-search-results';
    const optionId = (index) => `${listboxId}-${index}`;

    const isSearchable = !!manualId && query.trim().length >= MIN_QUERY_LENGTH;
    const isPanelOpen = isOpen && isSearchable;

    useEffect(() => {
        if (!isSearchable) {
            setResults([]);
            setTotal(0);
            return undefined;
        }

        const controller = new AbortController();
        const timer = setTimeout(async () => {
            try {
                const params = new URLSearchParams({
                    manual: manualId,
                    q: query.trim(),
                });
                const response = await fetch(`${endpoint}?${params}`, {
                    signal: controller.signal,
                });
                const data = await response.json();

                setResults(data.results || []);
                setTotal(data.total || 0);
                setActiveIndex(0);
                setIsOpen(openOnResultsRef.current);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setResults([]);
                    setTotal(0);
                }
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query, manualId, endpoint, isSearchable]);

    useEffect(() => {
        const handleClick = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    useEffect(() => {
        const previous = previousUrlQueryRef.current;
        previousUrlQueryRef.current = urlQuery;

        if (urlQuery) {
            openOnResultsRef.current = false;
            setQuery(urlQuery);
        } else if (previous) {
            setQuery('');
        }
    }, [urlQuery]);

    const clearSearch = () => {
        setQuery('');
        setIsOpen(false);
        inputRef.current?.focus();

        // Also ends the highlighting on the page, see ManualFindBar.
        if (router.query.q) {
            const { hash, pathname } = window.location;
            router.replace(pathname + hash, undefined, {
                shallow: true,
                scroll: false,
            });
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }

        if (!isOpen || !results.length) {
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex(
                (previous) =>
                    (previous + step + results.length) % results.length
            );
            return;
        }

        if (event.key === 'Enter') {
            // Click the anchor so Next handles navigation and the hash scroll.
            event.preventDefault();
            resultRefs.current[activeIndex]?.click();
        }
    };

    return (
        <div className={styles['ManualSearch']} ref={rootRef}>
            <div className={styles['ManualSearch__Field']} role="search">
                <input
                    ref={inputRef}
                    type="search"
                    className={styles['ManualSearch__Input']}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    role="combobox"
                    aria-expanded={isPanelOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        isPanelOpen && results.length
                            ? optionId(activeIndex)
                            : undefined
                    }
                    value={query}
                    onChange={(event) => {
                        openOnResultsRef.current = true;
                        setQuery(event.target.value);
                    }}
                    onFocus={() => results.length && setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                <span
                    className={styles['ManualSearch__Total']}
                    role="status"
                    aria-live="polite">
                    {isSearchable ? `${total} results` : ''}
                </span>
                {query ? (
                    <button
                        type="button"
                        className={styles['ManualSearch__Clear']}
                        aria-label="Clear search"
                        onClick={clearSearch}>
                        <Icon type="crossSmall" />
                    </button>
                ) : (
                    <span className={styles['ManualSearch__Icon']}>
                        <Icon
                            type="search"
                            size="medium"
                            modifiers={['ManualHeaderSearch']}
                        />
                    </span>
                )}
            </div>

            {isPanelOpen && (
                <div className={styles['ManualSearch__Panel']}>
                    <div
                        id={listboxId}
                        role="listbox"
                        aria-label={placeholder}
                        className={styles['ManualSearch__Results']}>
                        {results.length ? (
                            results.map((result, index) => (
                                <Link
                                    key={result.href + index}
                                    role="option"
                                    id={optionId(index)}
                                    aria-selected={index === activeIndex}
                                    href={withQuery(result.href, query.trim())}
                                    ref={(element) => {
                                        resultRefs.current[index] = element;
                                    }}
                                    className={classNames(
                                        styles['ManualSearch__Result'],
                                        {
                                            [styles[
                                                'ManualSearch__Result--Active'
                                            ]]: index === activeIndex,
                                        }
                                    )}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => setIsOpen(false)}>
                                    {!!result.icon && (
                                        <span
                                            className={
                                                styles[
                                                    'ManualSearch__ResultIcon'
                                                ]
                                            }>
                                            <Icon
                                                type={result.icon}
                                                modifiers={['ManualNavIcon']}
                                            />
                                        </span>
                                    )}
                                    <span
                                        className={
                                            styles['ManualSearch__Text']
                                        }>
                                        <span
                                            className={
                                                styles['ManualSearch__Chapter']
                                            }>
                                            {highlight(
                                                result.chapter_title,
                                                query.trim(),
                                                styles['ManualSearch__Mark']
                                            )}
                                        </span>
                                        <span
                                            className={
                                                styles['ManualSearch__Snippet']
                                            }>
                                            {!!result.section_title && (
                                                <span
                                                    className={
                                                        styles[
                                                            'ManualSearch__Section'
                                                        ]
                                                    }>
                                                    {highlight(
                                                        result.section_title,
                                                        query.trim(),
                                                        styles[
                                                            'ManualSearch__Mark'
                                                        ]
                                                    )}
                                                    {' · '}
                                                </span>
                                            )}
                                            {highlight(
                                                result.snippet,
                                                query.trim(),
                                                styles['ManualSearch__Mark']
                                            )}
                                        </span>
                                    </span>
                                </Link>
                            ))
                        ) : (
                            <p className={styles['ManualSearch__Empty']}>
                                No results for “{query.trim()}”
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

ManualSearch.propTypes = {
    manualId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    placeholder: PropTypes.string,
    endpoint: PropTypes.string,
};

export default ManualSearch;
